import { useLazyQuery, useMutation } from "@apollo/client/react"; import { useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { RenderInputByTypeMobile } from '../../../../components/formGroupGenerator/RenderInputByTypeMobile';
import { CustomButton } from '../../../../components/customButton/CustomButton';
import toast from 'react-hot-toast';
import { UPDATE_OBSERVACION_PACIENTE_ASISTENCIAL } from '../../../../data/graphQL/mutations/GestionAsistencial/updateObservacionPacienteAsistencial';
import GET_CONTRATOS_ASISTENCIAL_BY_PACIENTE from '../../../../data/graphQL/queries/GestionAsistencial/getContratosAsistencial';
import { GET_PRINT_INFORME_PACIENTE } from '../../../../data/graphQL/queries/GestionAsistencial/getPrintInformePaciente';
import GET_INFORME_ASISTENCIAL_PACIENTE, { adaptInformeAsistencialPaciente } from '../../../../data/graphQL/queries/GestionAsistencial/getInformeAsistencialPaciente';
import fileDownload from 'js-file-download';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { InformePacienteTemplate } from '../InformePacienteTemplate';
import { GET_INDICACIONES_BY_ID_EVALUACION } from '../../../../data/graphQL/queries/GestionAsistencial/getIndicacionesByIdEvaluacion';
import { PACIENTE_GEOPOSICIONAMIENTO_ACTUALIZADO } from "../../../../data/graphQL/queries/GestionAsistencial/getPacienteGeoActualizada";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const phoneRegex = /^\+54(9\d{10}|\d{10,11})$/;

export const TabPaciente = ({
  idPaciente,
  documentosOptions,
  evaluacionSelected,
  allowedWrite,
  pacienteData,
  setCambioDireccion
}) => {
  const [datosContacto, setDatosContacto] = useState({
    mail: pacienteData?.mail || '',
    familiarTelefono: pacienteData?.familiarTelefono || '',
    telefonoContacto: pacienteData?.telefonoContacto || '',
  });

  const [getPacienteGeoActualizada, { data: dataPacienteGeo }] = useLazyQuery(PACIENTE_GEOPOSICIONAMIENTO_ACTUALIZADO, {
    fetchPolicy: 'no-cache'
  });

  const geoChecked = useRef(false);

  useEffect(() => {
    if (
      !geoChecked.current &&
      pacienteData?.codigoSap &&
      evaluacionSelected?.idEvaluacion
    ) {
      geoChecked.current = true;

      // Verificar Geolocalización del paciente
      if (allowedWrite)
        getPacienteGeoActualizada({
          variables: {
            idPaciente,
            codPaciente: pacienteData.codigoSap,
            idEvaluacion: evaluacionSelected.idEvaluacion
          }
        });
    }
  }, [pacienteData?.codigoSap, evaluacionSelected?.idEvaluacion]);

  useEffect(() => {
    if (!dataPacienteGeo) return;
    if (dataPacienteGeo.pacienteGeoposicionActualizada.success && dataPacienteGeo.pacienteGeoposicionActualizada.valorEsperado) {
      setCambioDireccion(false);
    } else {
      setCambioDireccion(true);
    }
  }, [dataPacienteGeo]);

  const [guardar] = useMutation(UPDATE_OBSERVACION_PACIENTE_ASISTENCIAL, {
    refetchQueries: [GET_CONTRATOS_ASISTENCIAL_BY_PACIENTE],
    variables: {
      idEvaluacion: evaluacionSelected?.idEvaluacion,
      familiarTelefono: datosContacto.familiarTelefono,
      telefonoContacto: datosContacto.telefonoContacto,
      mail: datosContacto.mail
    }
  });

  // se ejecuta el get para copiar las indicaciones en caso de que se trate del primer ingreso
  // sin pasar antes por el tab de Indicaciones.
  const [getIndicaciones, { loading: loadingIndicaciones }] = useLazyQuery(
    GET_INDICACIONES_BY_ID_EVALUACION,
    {
      fetchPolicy: "no-cache"
    }
  );

  useEffect(() => {
    if (evaluacionSelected?.idEvaluacion > 0 && !loadingIndicaciones)
      getIndicaciones({ variables: { idEvaluacion: evaluacionSelected?.idEvaluacion } });
  }, [evaluacionSelected]);

  const updatedFields = useMemo(() => {
    return structuredClone(fields).
      map((input) => {
        const updatedInput = { ...input };
        switch (input.name) {
          case 'tipoDoc':
            updatedInput.options = documentosOptions;
            break;
          case 'numeroAfiliado':
            updatedInput.value = evaluacionSelected?.numeroAfiliado;
            break;
          default:
            break;
        }
        if (!allowedWrite) {
          updatedInput.disabled = true;
        }
        return updatedInput;
      });
  }, [documentosOptions, evaluacionSelected]);

  useEffect(() => {
    setDatosContacto({
      mail: pacienteData?.mail || '',
      familiarTelefono: pacienteData?.familiarTelefono || '',
      telefonoContacto: pacienteData?.telefonoContacto || '',
    });
  }, [pacienteData]);

  const handleSubmit = () => {
    const errorMessages = [];

    if (!!datosContacto.mail) {
      const emails = datosContacto.mail.split(';').map((e) => e.trim());
      const emailsNoVacios = emails.filter((e) => e.length > 0);

      if (emailsNoVacios.length > 2) {
        errorMessages.push('Puede ingresar máximo 2 emails separados por ";".\n');
      } else {
        const emailsInvalidos = emailsNoVacios.filter((e) => !emailRegex.test(e));
        if (emailsInvalidos.length > 0) {
          errorMessages.push('Debe ingresar emails válidos. Emails inválidos: ' + emailsInvalidos.join(', ') + '\n');
        }
      }
    }

    if (datosContacto.telefonoContacto && !phoneRegex.test(datosContacto.telefonoContacto)) {
      errorMessages.push('Debe ingresar un télefono de paciente completo con código de país. Ej: +541123456789.\n');
    }

    if (datosContacto.familiarTelefono && !phoneRegex.test(datosContacto.familiarTelefono)) {
      errorMessages.push('Debe ingresar un télefono de familiar completo con código de país. Ej: +541123456789.\n');
    }

    if (errorMessages.length) {
      return toast.error(errorMessages.join(''));
    }

    toast.promise(
      guardar(), {
      loading: 'Guardando "Paciente"...',
      success: '"Paciente" guardado.',
      error: 'Ocurrió un error al intentar guardar "Paciente".'
    }
    );
  };

  const handleChange = (name, value, inputEditable) => {
    if (!inputEditable) return null;

    setDatosContacto((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const [getInformeAsistencialPaciente] = useLazyQuery(GET_INFORME_ASISTENCIAL_PACIENTE, {
    fetchPolicy: 'no-cache'
  });

  const handlePrint = async (idEvaluacion) => {
    try {
      const informe = await getInformeAsistencialPaciente({
        variables: {
          idEvaluacion: idEvaluacion
        }
      });

      if (!informe?.data?.informeAsistencialPaciente) {
        toast.error('Es necesaria más información para descargar el informe.');
      } else {
        let htmlPdf = "";

        const informeHTML = document.createElement('div');
        const root = createRoot(informeHTML);
        flushSync(() => {
          root.render(<InformePacienteTemplate data={adaptInformeAsistencialPaciente(informe?.data?.informeAsistencialPaciente)} />);
        });
        htmlPdf += informeHTML.innerHTML;

        getPrintInforme({
          variables: {
            html: htmlPdf
          }
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const [getPrintInforme, { data: dataPrintInforme }] = useLazyQuery(GET_PRINT_INFORME_PACIENTE, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataPrintInforme) return;
    if (dataPrintInforme.printInformeAsistencialPaciente != null) {
      const arr = new Uint8Array(dataPrintInforme.printInformeAsistencialPaciente);
      const documento = new Blob([arr], { type: 'application/pdf' });
      const fileName = "PresentacionServicio-" + evaluacionSelected?.idEvaluacion + ".pdf";
      fileDownload(documento, fileName);
    } else {
      throw new Error('error');
    }
  }, [dataPrintInforme]);

  return (
    <div className='generic-tab-container'>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <div className='button-save-geo-data'>
            {allowedWrite && <CustomButton
              onClick={handleSubmit}
              label='Guardar'
              variant='outlined' />
            }
            <CustomButton
              sx={{ marginLeft: '20px' }}
              onClick={() => toast.promise(handlePrint(evaluacionSelected?.idEvaluacion), {
                loading: 'Descargando informe...',
                success: <label>Informe descargado</label>,
                error: <label>No se pudo descargar el informe.</label>
              })}
              label='Imprimir'
              variant='contained' />

          </div>
        </Grid>
        {updatedFields.
          map((input, index) =>
            <Grid key={`input-${input.name}-index-${index}`} size={{ md: input.col, xs: input.type !== 'item' ? 12 : 0 }}>
              <RenderInputByTypeMobile
                key={`input-${input.name}-index-${index}`}
                input={input}
                handleChange={(name, value) => handleChange(name, value, input.editable)}
                value={input.editable ? datosContacto[input.name] : pacienteData[input.name]}
              />
            </Grid>
          )}
      </Grid>
    </div>);

};

const fields = [
  //row
  {
    displayName: 'Nombre',
    name: 'nombre',
    type: 'string',
    disabled: true,
    col: 3
  },
  {
    displayName: 'Apellido',
    name: 'apellido',
    type: 'string',
    disabled: true,
    col: 3
  },
  {
    displayName: 'Nro. Afiliado',
    name: 'numeroAfiliado',
    type: 'string',
    disabled: true,
    col: 3
  },
  {
    displayName: 'Item',
    name: 'item',
    type: 'item',
    col: 3
  },
  //row
  {
    displayName: "Tipo Documento",
    name: "tipoDoc",
    type: "select",
    disabled: true,
    col: 2
  },
  {
    displayName: "Número Documento",
    name: "nroDocumento",
    type: "int",
    disabled: true,
    col: 2
  },
  {
    displayName: "F. nacimiento",
    name: "fechaNacimiento",
    type: "date",
    disabled: true,
    col: 2
  },
  {
    displayName: 'Sexo',
    name: 'sexo',
    type: 'button_options',
    required: true,
    disabled: true,
    fitContent: true,
    options: [
      { id: 'F', displayName: 'F' },
      { id: 'M', displayName: 'M' }],

    col: 1.5
  },
  {
    name: 'item',
    col: 4
  },
  //row
  {
    displayName: "Código SAP",
    name: "codigoSap",
    type: "string",
    disabled: true,
    col: 2
  },
  {
    displayName: "Financiador",
    name: "financiador",
    type: "string",
    disabled: true,
    col: 5
  },
  { name: 'item', col: 5 },
  //row
  {
    displayName: "Familiar a cargo",
    name: "familiar",
    type: "string",
    disabled: true,
    col: 3
  },
  {
    displayName: "Teléfono Familiar",
    name: "familiarTelefono",
    editable: true,
    type: "string",
    col: 3
  },
  {
    displayName: "Teléfono paciente",
    name: "telefonoContacto",
    type: "string",
    editable: true,
    col: 3
  },
  {
    displayName: "Mail",
    name: "mail",
    editable: true,
    type: "string",
    col: 3
  },
  //row
  {
    displayName: "Observación",
    name: "observaciones",
    type: "long_string",
    disabled: true,
    col: 12
  }];